// JOBE-0105: useProjectsListQuery — bespoke fetch hook for the
// /projects index. Calls `GET /api/projects?scope=<scope>&expand=rollups`
// directly (the EntityBrowser host's default `/api/entities` fetch is
// dead weight on this surface — see spec §"Decision 5" + the hook-
// return field-consumption table).
//
// Inputs:
//   - scope: one of 'mine' | 'watching' | 'department' | 'org'
//   - chips: array of chip objects (from useEntityBrowserState). The
//     `/api/projects` endpoint is project-only by definition, so no
//     `type=project` pin is injected — the chip set carries only
//     user-authored predicates plus the library's scope chip.
//   - sort: a 'lastChangedAt:desc' / 'name:asc' etc. value. v1 wires it
//     onto the URL only; the BFF list route does not honour `?sort=` yet
//     (JOBE-0050.1 returns lastChangedAt:desc by default). Passed through
//     here for forward-compat — the BFF widening is a one-flag flip.
//   - cursor: opaque cursor string for Load More.
//
// Returns: { rows, nextCursor, loadState, error, retry, rollupsTruncated }
//   - loadState:
//       'idle'         — initial render before first fetch
//       'loading'      — fetch in flight
//       'ready'        — fetch resolved
//       'cedar-denied' — 403 (per ADR-0008 / JOBE-0067.6)
//       'error'        — non-403 failure
//
// `cursor`-change semantics: a non-null cursor APPENDS to the existing
// rows (load more). Scope/chip/sort changes flow through the caller's
// useEntityBrowserUrlState — that hook clears `cursor` whenever chips /
// sort mutate (cursor-lifecycle invariant) — so this hook only needs to
// distinguish "cursor null → fresh fetch, replace rows" from "cursor
// set → append page".

import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { chipsToFetchQuery } from '../library/chipVocabulary.js';

const VALID_SCOPES = new Set(['mine', 'watching', 'department', 'org']);

// chipsToQuery: filter out the scope chip (kind === 'scope'), then
// defer to the library's canonical chips-to-fetch-query translator for
// everything else. The `/api/projects` endpoint is project-only by
// definition, so no type-pin coercion is needed here.
function chipsToQuery(chips) {
  if (!Array.isArray(chips)) return new URLSearchParams();
  const fetchChips = chips.filter((c) => {
    if (!c) return false;
    if (c.kind === 'scope') return false;
    return true;
  });
  return chipsToFetchQuery(fetchChips);
}

export function buildProjectsListURL({ scope, chips, sort, cursor }) {
  const params = chipsToQuery(chips);
  // Emit scope verbatim (including 'mine' — the BFF tolerates it as a
  // no-op; URL stays explicit for the caller).
  if (scope && VALID_SCOPES.has(scope)) {
    params.set('scope', scope);
  }
  params.set('expand', 'rollups');
  if (sort && sort !== 'lastChangedAt:desc') {
    params.set('sort', sort);
  }
  if (cursor) {
    params.set('cursor', cursor);
  }
  return `/api/projects?${params.toString()}`;
}

export function useProjectsListQuery({ scope, chips, sort, cursor }) {
  const [rows, setRows] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loadState, setLoadState] = useState('idle');
  const [error, setError] = useState(null);
  const [rollupsTruncated, setRollupsTruncated] = useState(false);
  const [tick, setTick] = useState(0);

  // Memoize the chip slice as a string signature so chip-array identity
  // changes (without semantic change) don't re-fetch.
  const chipsSig = useMemo(() => {
    if (!Array.isArray(chips)) return '';
    return chips
      .filter((c) => c && c.kind === 'predicate')
      .map((c) => `${c.friendlyToken}=${(c.values || []).join(',')}`)
      .sort()
      .join('&');
  }, [chips]);

  const retry = useCallback(() => setTick((t) => t + 1), []);

  // Track whether the current cursor is a "load more" (append) or a
  // fresh fetch (replace). The previous-cursor ref distinguishes the
  // two: when the previous value was null and we now have a non-null
  // cursor (or vice versa), we know the caller is paging vs re-querying.
  const prevSigRef = useRef({ scope: null, chipsSig: '', sort: null });

  useEffect(() => {
    const ctrl = new AbortController();
    let alive = true;
    const url = buildProjectsListURL({ scope, chips, sort, cursor });

    // Determine append-vs-replace BEFORE clearing state.
    const sigChanged = (
      prevSigRef.current.scope !== scope
      || prevSigRef.current.chipsSig !== chipsSig
      || prevSigRef.current.sort !== sort
    );
    const isLoadMore = Boolean(cursor) && !sigChanged;
    prevSigRef.current = { scope, chipsSig, sort };

    if (!isLoadMore) {
      setRows([]);
      setNextCursor(null);
      setRollupsTruncated(false);
    }
    setLoadState('loading');
    setError(null);

    fetch(url, { credentials: 'same-origin', signal: ctrl.signal })
      .then(async (res) => {
        if (!alive) return;
        if (res.status === 403) {
          setLoadState('cedar-denied');
          return;
        }
        if (!res.ok) {
          setError({ status: res.status });
          setLoadState('error');
          return;
        }
        const body = await res.json();
        if (!alive) return;
        const items = Array.isArray(body?.items) ? body.items : [];
        if (isLoadMore) {
          setRows((prev) => prev.concat(items));
        } else {
          setRows(items);
        }
        setNextCursor(body?.next_cursor || null);
        if (body?.rollups_truncated === true) {
          setRollupsTruncated(true);
        }
        setLoadState('ready');
      })
      .catch((err) => {
        if (!alive || err?.name === 'AbortError') return;
        setError({ status: 0, message: err?.message || String(err) });
        setLoadState('error');
      });

    return () => {
      alive = false;
      ctrl.abort();
    };
    // chips identity changes are subsumed by chipsSig; ESLint can't see that.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, chipsSig, sort, cursor, tick]);

  return { rows, nextCursor, loadState, error, retry, rollupsTruncated };
}
